import React, { useEffect, useState, useMemo } from "react";
import { Search, ChevronDown, ChevronUp, UserX, UserCheck, Key, ArrowRightLeft, LogOut, Users, Send } from "lucide-react";
import StatusBadge from "../../ui/StatusBadge";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";

const ROLE_STYLE = {
  superadmin: "text-[#fbbf24] bg-[#fbbf24]/10 border-[#fbbf24]/20",
  executive:  "text-[#e879f9] bg-[#e879f9]/10 border-[#e879f9]/20",
  admin:      "text-[#60a5fa] bg-[#60a5fa]/10 border-[#60a5fa]/20",
  manager:    "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/20",
  staff:      "text-white/50 bg-white/5 border-white/10",
};

const BULK_ACTIONS = [
  { key: "deactivate",   label: "Deactivate",         icon: UserX,          color: "text-red-400"    },
  { key: "reactivate",   label: "Reactivate",          icon: UserCheck,      color: "text-[#00ff90]"  },
  { key: "change_role",  label: "Change Role",         icon: Users,          color: "text-purple-400" },
  { key: "reset_pw",     label: "Reset Password",      icon: Key,            color: "text-yellow-400" },
  { key: "transfer",     label: "Transfer Department", icon: ArrowRightLeft, color: "text-blue-400"   },
  { key: "change_manager", label: "Change Manager",   icon: Users,          color: "text-cyan-400"   },
  { key: "offboard",     label: "Offboard",            icon: LogOut,         color: "text-orange-400" },
];

export default function UserList({ onOpenUser }) {
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [selected,  setSelected]  = useState(new Set());
  const [sortKey,   setSortKey]   = useState("name");
  const [sortDir,   setSortDir]   = useState("asc");
  const [bulkModal,     setBulkModal]     = useState(null); // action key
  const [bulkInput,     setBulkInput]     = useState("");
  const [bulkBusy,      setBulkBusy]      = useState(false);
  const [bulkMsg,       setBulkMsg]       = useState(null);
  const [bulkTempPws,   setBulkTempPws]   = useState(null); // {uid: pw}
  const [managers,      setManagers]      = useState([]);
  const [resendingId,   setResendingId]   = useState(null); // uid currently in-flight
  const [rowNotice,     setRowNotice]     = useState(null); // { tone: "ok"|"err", text }
  const [showInactive,  setShowInactive]  = useState(false); // hide deactivated by default
  const [togglingId,    setTogglingId]    = useState(null);  // uid mid-reactivate

  const [filters, setFilters] = useState({
    search: "", department: "ALL", role: "ALL", status: "ALL", location: "ALL",
  });

  const token = localStorage.getItem("token");

  // active=true excludes inactive/deactivated rows; active=ALL includes
  // everything (backend contract, confirmed in list_users). Kept as a
  // client-driven default so other consumers of the endpoint aren't
  // affected.
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const activeParam = showInactive ? "ALL" : "true";
      const res = await fetch(`${API_BASE}/api/admin/users?active=${activeParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : data?.users || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [showInactive]);

  // Derived filter options
  const departments = useMemo(() => ["ALL", ...new Set(users.map(u => u.department).filter(Boolean))], [users]);
  const locations   = useMemo(() => ["ALL", ...new Set(users.map(u => u.location).filter(Boolean))],   [users]);

  // Filtered + sorted
  const visible = useMemo(() => {
    let out = [...users];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      out = out.filter(u =>
        (u.name || u.full_name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.employee_code || u.employee_id || "").toLowerCase().includes(q)
      );
    }
    if (filters.department !== "ALL") out = out.filter(u => u.department === filters.department);
    if (filters.role       !== "ALL") out = out.filter(u => u.role === filters.role);
    if (filters.status     !== "ALL") out = out.filter(u => u.status === filters.status);
    if (filters.location   !== "ALL") out = out.filter(u => u.location === filters.location);

    out.sort((a, b) => {
      const va = String(a[sortKey] || "").toLowerCase();
      const vb = String(b[sortKey] || "").toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return out;
  }, [users, filters, sortKey, sortDir]);

  const allSelected  = visible.length > 0 && visible.every(u => selected.has(u.id));
  const someSelected = visible.some(u => selected.has(u.id));

  const toggleAll  = () => setSelected(allSelected ? new Set() : new Set(visible.map(u => u.id)));
  const toggleOne  = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const sort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ k }) => sortKey === k
    ? (sortDir === "asc" ? <ChevronUp size={11} /> : <ChevronDown size={11} />)
    : null;

  // UI action keys → wire keys accepted by POST /api/admin/users/bulk-action.
  // Backend allows:
  //   activate | deactivate | reactivate | change_role |
  //   reset_password | transfer_department | change_manager | offboard
  // The UI uses a few shorter labels — remap them here, everything else
  // passes through unchanged.
  const ACTION_WIRE = {
    reset_pw: "reset_password",
    transfer: "transfer_department",
    // reactivate stays as-is — backend supports it directly.
  };

  // Per-action map of which named body field carries the modal input.
  // Pre-fix the frontend was sending everything as `value:` regardless,
  // which is why change_role / transfer / change_manager were silently
  // ignored once the 422 was fixed.
  const INPUT_FIELD = {
    change_role:    "role",
    transfer:       "department",
    change_manager: "manager_id",
  };

  // Load the manager dropdown lazily — only when the admin opens the
  // change_manager modal. Cached in state for the page lifetime.
  useEffect(() => {
    if (bulkModal !== "change_manager") return;
    if (managers.length > 0) return;
    fetch(`${API_BASE}/api/admin/users?role=manager`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setManagers(Array.isArray(rows) ? rows : []))
      .catch(() => setManagers([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkModal]);

  const runBulkAction = async () => {
    if (!selected.size) return;
    setBulkBusy(true); setBulkMsg(null); setBulkTempPws(null);
    try {
      const wireAction = ACTION_WIRE[bulkModal] || bulkModal;
      const body = { action: wireAction, user_ids: [...selected] };

      const field = INPUT_FIELD[bulkModal];
      if (field) {
        if (!bulkInput || !String(bulkInput).trim()) {
          throw new Error("Please fill in the required field");
        }
        body[field] = String(bulkInput).trim();
      }

      const res = await fetch(`${API_BASE}/api/admin/users/bulk-action`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const errBody = await res.json();
          if (errBody?.detail) detail = typeof errBody.detail === "string" ? errBody.detail : JSON.stringify(errBody.detail);
        } catch { /* non-JSON body */ }
        throw new Error(detail);
      }

      // Surface temp passwords from reset_password so the admin can
      // hand them over. The backend wraps the payload in {data: {…}}.
      const responseBody = await res.json().catch(() => ({}));
      const data         = responseBody?.data || responseBody;
      const results      = data?.results || {};
      if (results?.temp_passwords && Object.keys(results.temp_passwords).length) {
        setBulkTempPws(results.temp_passwords);
        setBulkMsg(`Done ✓ — ${results.succeeded} updated · share the temp passwords below`);
      } else {
        const fail = results?.failed ?? 0;
        setBulkMsg(`Done ✓ — ${results?.succeeded ?? 0} updated${fail ? ` · ${fail} failed` : ""}`);
      }
      setBulkInput("");
      await load();
      setSelected(new Set());
      // Auto-close unless we're holding temp passwords the admin needs
      // to copy.
      if (!results?.temp_passwords) {
        setTimeout(() => { setBulkModal(null); setBulkMsg(null); }, 1500);
      }
    } catch (e) { setBulkMsg(`Error: ${e.message}`); }
    finally { setBulkBusy(false); }
  };

  const selectedAction = BULK_ACTIONS.find(a => a.key === bulkModal);
  const needsInput = ["change_role","transfer","change_manager"].includes(bulkModal);

  // Per-row: reactivate a previously deactivated (soft-deleted) user.
  // Uses the same PATCH endpoint the superadmin list has always used; no
  // new server work. Only offered when "Show inactive" surfaces the row.
  const reactivateUser = async (u) => {
    if (!u?.id || togglingId) return;
    setTogglingId(u.id);
    setRowNotice(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: true }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail || `HTTP ${res.status}`);
      }
      // Optimistic: flip locally so the badge and row-action update
      // right away without waiting for a refetch.
      setUsers((rows) => rows.map((r) =>
        r.id === u.id ? { ...r, is_active: true, status: "active" } : r
      ));
      setRowNotice({ tone: "ok", text: `Reactivated ${u.email || "user"}.` });
    } catch (e) {
      setRowNotice({ tone: "err", text: e.message || "Could not reactivate." });
    } finally {
      setTogglingId(null);
      setTimeout(() => setRowNotice(null), 3500);
    }
  };

  // Per-row: resend the set-password link for a PENDING user. Reuses the
  // same status-derived notion of "pending" the list already displays via
  // <StatusBadge>. Backend returns 400 for users who've already logged in.
  const resendInvite = async (u) => {
    if (!u?.id || resendingId) return;
    setResendingId(u.id);
    setRowNotice(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${u.id}/resend-invite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      const data = body?.data || body;
      if (!res.ok) {
        const detail = typeof body?.detail === "string" ? body.detail
                     : typeof data?.message === "string" ? data.message
                     : `HTTP ${res.status}`;
        setRowNotice({ tone: "err", text: detail });
        return;
      }
      setRowNotice({
        tone: "ok",
        text: data?.message || `Invite re-sent to ${u.email}.`,
      });
    } catch (e) {
      setRowNotice({ tone: "err", text: e.message || "Network error." });
    } finally {
      setResendingId(null);
      setTimeout(() => setRowNotice(null), 3500);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-[#00ff90]/40 shrink-0" />
          <input className={`${inputCls} flex-1`} placeholder="Search name, email, ID…"
            value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
        {[
          { key: "department", opts: departments },
          { key: "role", opts: ["ALL","superadmin","executive","admin","manager","staff"] },
          { key: "status",     opts: ["ALL","pending","active","inactive","offboarded"] },
          { key: "location",   opts: locations },
        ].map(({ key, opts }) => (
          <select key={key} className={inputCls} value={filters[key]}
            onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}>
            {opts.map(o => <option key={o} value={o}>{o === "ALL" ? `All ${key}s` : o}</option>)}
          </select>
        ))}
        {/* Show-inactive drives the FETCH (backend `active=` param); the
            status dropdown filters WITHIN what's fetched. Picking
            "inactive" while the toggle is off will match no rows — the
            toggle needs to be on for those to be pulled at all. */}
        <label className="inline-flex items-center gap-2 text-xs text-white/70 select-none cursor-pointer px-2"
               title="Include deactivated users in the list">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 rounded accent-[#00ff90]"
          />
          Show inactive
        </label>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="flex items-center gap-2 flex-wrap rounded-xl border border-[#00ff90]/20 bg-[#00ff90]/5 px-4 py-3">
          <span className="text-xs text-[#00ff90]/70 font-medium">{selected.size} selected</span>
          <span className="text-white/20">·</span>
          {BULK_ACTIONS.map(({ key, label, icon: Icon, color }) => (
            <button key={key} onClick={() => { setBulkModal(key); setBulkInput(""); setBulkMsg(null); }}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors ${color}`}>
              <Icon size={12} />{label}
            </button>
          ))}
          <button onClick={() => setSelected(new Set())} className="text-xs text-white/30 hover:text-white ml-auto transition-colors">Clear</button>
        </div>
      )}

      {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>}

      {rowNotice && (
        <div className={
          "rounded-xl border p-3 text-sm " +
          (rowNotice.tone === "ok"
            ? "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"
            : "border-red-500/30 bg-red-500/10 text-red-300")
        }>
          {rowNotice.tone === "ok" ? "✓ " : "⚠ "}{rowNotice.text}
        </div>
      )}

      {/* Table */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Users</span>
          <span className="text-xs text-white/30">{visible.length} of {users.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                    className="w-4 h-4 rounded accent-[#00ff90]" />
                </th>
                {[
                  { key: "name",       label: "Name"       },
                  { key: "email",      label: "Email"      },
                  { key: "department", label: "Dept"       },
                  { key: "role",       label: "Role"       },
                  { key: "status",     label: "Status"     },
                  { key: "location",   label: "Location"   },
                  { key: "start_date", label: "Start Date" },
                ].map(({ key, label }) => (
                  <th key={key} className="px-3 py-3 text-left">
                    <button onClick={() => sort(key)}
                      className="flex items-center gap-1 text-xs text-[#00ff90]/40 uppercase tracking-wider hover:text-[#00ff90]/70 transition-colors">
                      {label}<SortIcon k={key} />
                    </button>
                  </th>
                ))}
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}><td colSpan={9} className="px-4 py-3"><div className="h-8 rounded bg-white/5 animate-pulse" /></td></tr>
                ))
              ) : visible.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-white/20 italic text-sm">No users found.</td></tr>
              ) : visible.map(u => {
                const name = u.name || u.full_name || "—";
                const status = (u.status || "active").toLowerCase();
                const role   = (u.role || "staff").toLowerCase();
                return (
                  <tr key={u.id} className={`border-b border-white/5 hover:bg-white/3 transition-colors ${selected.has(u.id) ? "bg-[#00ff90]/3" : ""}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleOne(u.id)}
                        className="w-4 h-4 rounded accent-[#00ff90]" />
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => onOpenUser?.(u)} className="text-left hover:text-[#00ff90] transition-colors">
                        <p className="text-sm text-white font-medium">{name}</p>
                        <p className="text-xs text-white/30">{u.employee_code || u.employee_id || ""}</p>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-white/50 text-xs">{u.email}</td>
                    <td className="px-3 py-3 text-white/50 text-xs">{u.department || "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role || "staff"])
                          .map(r => r.toLowerCase())
                          .map(r => (
                            <span key={r} className={`text-xs px-2 py-0.5 rounded-full border capitalize ${ROLE_STYLE[r] || ROLE_STYLE.staff}`}>
                              {r}
                            </span>
                          ))
                        }
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={status} />
                    </td>
                    <td className="px-3 py-3 text-white/40 text-xs">{u.location || "—"}</td>
                    <td className="px-3 py-3 text-white/30 text-xs">{u.start_date || "—"}</td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        {status === "pending" && (
                          <button onClick={() => resendInvite(u)}
                            disabled={resendingId === u.id}
                            title="Send a fresh set-password email (1h link)"
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-amber-400/30 bg-amber-400/10 text-amber-400 hover:bg-amber-400/20 transition-colors disabled:opacity-40 disabled:cursor-wait">
                            <Send size={12} />
                            {resendingId === u.id ? "Sending…" : "Resend invite"}
                          </button>
                        )}
                        {status === "inactive" && (
                          <button onClick={() => reactivateUser(u)}
                            disabled={togglingId === u.id}
                            title="Reactivate this user"
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors disabled:opacity-40 disabled:cursor-wait">
                            <UserCheck size={12} />
                            {togglingId === u.id ? "Reactivating…" : "Reactivate"}
                          </button>
                        )}
                        <button onClick={() => onOpenUser?.(u)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bulk action modal */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="w-full max-w-md rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_40px_#00ff9022] p-6 space-y-4">
            <h3 className="text-sm font-semibold text-white">{selectedAction?.label} · {selected.size} user{selected.size !== 1 ? "s" : ""}</h3>

            {needsInput && (
              <div>
                <label className="text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5">
                  {bulkModal === "change_role"    && "New Role"}
                  {bulkModal === "transfer"       && "New Department"}
                  {bulkModal === "change_manager" && "New Manager"}
                </label>
                {bulkModal === "change_role" ? (
                  <select value={bulkInput} onChange={(e) => setBulkInput(e.target.value)}
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                    <option value="">— select a role —</option>
                    {["staff", "manager", "admin", "executive"].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                ) : bulkModal === "change_manager" ? (
                  <select value={bulkInput} onChange={(e) => setBulkInput(e.target.value)}
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors">
                    <option value="">— select a manager —</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {(m.full_name || m.name || m.email)}{m.email ? ` · ${m.email}` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input value={bulkInput} onChange={(e) => setBulkInput(e.target.value)}
                    placeholder="e.g. Engineering"
                    className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors" />
                )}
              </div>
            )}

            {bulkModal === "offboard" && (
              <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3 text-xs text-orange-400/80">
                ⚠ This will trigger the offboarding checklist for all selected users — final payslip, access revocation and document archival.
              </div>
            )}

            {bulkTempPws && (
              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-2">
                <p className="text-xs text-yellow-300/80 uppercase tracking-wider font-medium">
                  Temporary passwords — copy now
                </p>
                <p className="text-[10px] text-yellow-200/50">
                  These are shown once. Share through a secure channel.
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {Object.entries(bulkTempPws).map(([uid, pw]) => (
                    <div key={uid} className="flex items-center gap-2 text-xs">
                      <span className="text-white/40 font-mono">{uid.slice(0, 8)}…</span>
                      <code className="flex-1 font-mono text-[#00ff90] bg-black/60 border border-white/10 rounded px-2 py-1 select-all">
                        {pw}
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bulkMsg && <p className={`text-xs ${bulkMsg.startsWith("Error") ? "text-red-400" : "text-[#00ff90]"}`}>{bulkMsg}</p>}

            <div className="flex gap-2 justify-end">
              <button onClick={() => { setBulkModal(null); setBulkTempPws(null); setBulkMsg(null); }}
                className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                {bulkTempPws ? "Close" : "Cancel"}
              </button>
              <button onClick={runBulkAction} disabled={bulkBusy}
                className={`text-xs px-5 py-2 rounded-lg border disabled:opacity-40 transition-colors font-medium ${
                  bulkModal === "offboard" || bulkModal === "deactivate"
                    ? "border-red-500/30 bg-red-500/15 text-red-400 hover:bg-red-500/25"
                    : "border-[#00ff90]/30 bg-[#00ff90]/15 text-[#00ff90] hover:bg-[#00ff90]/25"
                }`}>
                {bulkBusy ? "Processing…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}